import { useState, useEffect, useRef } from "react";
import { useForm, ValidationError } from "@formspree/react";
import { ArrowRight } from "lucide-react";
import {
  PageWrapper,
  ContactContainer,
  LeftSection,
  Eyebrow,
  Heading,
  Subtitle,
  EmailLink,
  OrText,
  RightSection,
  FormContainer,
  FormRow,
  InputGroup,
  Label,
  Input,
  Select,
  TextArea,
  SubmitButton,
  ButtonArrow,
} from "./contact.styles";
import { Toaster } from "../../components/toaster/toaster";
import { toast } from "react-toastify";
import { trackEvent } from "../../helpers";

export const Contact = () => {
  const [state, handleSubmit] = useForm("xzblnjbw");
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [goal, setGoal] = useState<string>("mvp");
  const [timeline, setTimeline] = useState<string>("short");
  const [message, setMessage] = useState<string>("");

  const isMounted = useRef(false);

  const notify = () => {
    if (!isMounted.current) {
      toast.info("Thank you! I'll be in touch within 24 hours.", {
        position: "bottom-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
      });
      isMounted.current = true;
    }
  };

  const isDisabled =
    name.trim().length === 0 ||
    email.trim().length === 0 ||
    goal.trim().length === 0 ||
    timeline.trim().length === 0;

  const resetForm = () => {
    setName("");
    setEmail("");
    setGoal("mvp");
    setTimeline("short");
    setMessage("");
  };

  useEffect(() => {
    if (state.succeeded) {
      notify();
      resetForm();
    }
  }, [state.succeeded]);

  useEffect(() => {
    // Load Calendly CSS
    const link = document.createElement("link");
    link.href = "https://assets.calendly.com/assets/external/widget.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);

    // Load Calendly script
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.head.removeChild(link);
      document.body.removeChild(script);
    };
  }, []);

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/reneelouise-nzems/30min",
      });
    }
    trackEvent("Contact - Book a discovery call click");
  };

  return (
    <PageWrapper>
      <main>
        <ContactContainer>
          {/* Left Section */}
          <LeftSection>
            <Eyebrow>CONTACT</Eyebrow>
            <Heading>Let's build <em>it.</em></Heading>
            <Subtitle>
              Tell me what you are building and where it is stuck. I reply within 24 hours.
            </Subtitle>
            <EmailLink href="mailto:hello@reneelouise.tech">
              hello@reneelouise.tech
            </EmailLink>
            <OrText
              as="button"
              onClick={openCalendly}
              style={{ background: "none", border: "none", textAlign: "left" }}
            >
              OR BOOK A DISCOVERY CALL <ArrowRight size={14} color="#D9442B" style={{ marginLeft: '4px' }} />
            </OrText>
          </LeftSection>

          {/* Right Section - Form */}
          <RightSection>
            <FormContainer onSubmit={handleSubmit}>
              <FormRow>
                <InputGroup>
                  <Label>NAME</Label>
                  <Input
                    name="name"
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                  <ValidationError field="name" errors={state.errors} />
                </InputGroup>

                <InputGroup>
                  <Label>EMAIL</Label>
                  <Input
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <ValidationError field="email" errors={state.errors} />
                </InputGroup>
              </FormRow>

              <FormRow>
                <InputGroup>
                  <Label>WHAT DO YOU NEED?</Label>
                  <Select
                    name="goal"
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    required
                  >
                    <option value="mvp">Build an MVP</option>
                    <option value="audit">Technical audit</option>
                    <option value="ai-integration">AI integration</option>
                    <option value="architecture">System architecture</option>
                    <option value="testing">Testing & CI/CD</option>
                    <option value="leadership">Technical leadership</option>
                    <option value="consulting">General consulting</option>
                    <option value="other">Other</option>
                  </Select>
                  <ValidationError field="goal" errors={state.errors} />
                </InputGroup>

                <InputGroup>
                  <Label>TIMELINE</Label>
                  <Select
                    name="timeline"
                    value={timeline}
                    onChange={(e) => setTimeline(e.target.value)}
                    required
                  >
                    <option value="short">1 to 3 months</option>
                    <option value="immediate">Immediate (within 2 weeks)</option>
                    <option value="medium">3 to 6 months</option>
                    <option value="long">6+ months</option>
                    <option value="exploring">Just exploring</option>
                  </Select>
                  <ValidationError field="timeline" errors={state.errors} />
                </InputGroup>
              </FormRow>

              <InputGroup>
                <Label>MESSAGE</Label>
                <TextArea
                  name="message"
                  placeholder="Where is your product now, and what is in the way?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                />
                <ValidationError field="message" errors={state.errors} />
              </InputGroup>

              {state.succeeded && <Toaster />}

              <SubmitButton
                isDisabled={isDisabled}
                disabled={state.submitting || isDisabled}
                onClick={() => trackEvent("Contact form submitted")}
              >
                {state.submitting ? "Sending..." : "Send enquiry"}
                {!state.submitting && <ButtonArrow aria-hidden="true"><ArrowRight size={16} color="#D9442B" /></ButtonArrow>}
              </SubmitButton>
            </FormContainer>
          </RightSection>
        </ContactContainer>
      </main>
    </PageWrapper>
  );
};
